import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Wand2,
  X,
  Send,
  CheckCircle2,
  TriangleAlert,
  Sparkles,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const INITIAL_FORM = {
  full_name: "",
  phone: "",
  email: "",
  service_address: "",
  zip: "",
  bin_count: 2,
  message: "",
};

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e) => setReduced(e.matches);
    if (mq.addEventListener) mq.addEventListener("change", handler);
    else mq.addListener(handler);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", handler);
      else mq.removeListener(handler);
    };
  }, []);
  return reduced;
}

/**
 * Wide "Need a Custom Plan?" banner. Collapsed by default; expands inline with
 * a fade+height animation. Posts to /api/custom-quote with source="plans_custom"
 * so admin can distinguish submissions. Reused on /plans and Home.
 */
export default function CustomPlanBanner({ compact = false, eyebrow }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const reduced = useReducedMotion();

  const update = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (form.full_name.trim().length < 2) e.full_name = "Tell us your name.";
    if (form.phone.trim().length < 4) e.phone = "Phone required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email.";
    if (form.service_address.trim().length < 2)
      e.service_address = "Street address required.";
    if (!/^\d{5}$/.test(form.zip)) e.zip = "5-digit zip.";
    if (form.message.trim().length < 4)
      e.message = "Tell us what you need (at least a few words).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await api.post("/custom-quote", {
        full_name: form.full_name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim().toLowerCase(),
        service_address: form.service_address.trim(),
        zip: form.zip,
        bin_count: form.bin_count,
        message: form.message.trim(),
        source: "plans_custom",
      });
      setDone(true);
      setOpen(false);
      setForm(INITIAL_FORM);
    } catch (err) {
      toast.error("Couldn't send your request", {
        description: formatApiError(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldDelay = (i) =>
    reduced ? "0ms" : open ? `${80 + i * 40}ms` : "0ms";

  return (
    <div
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 shadow-2xl shadow-brand-500/15 ${
        compact ? "mt-10" : "mt-12 lg:mt-14"
      }`}
      data-testid="custom-plan-banner"
      data-state={done ? "success" : open ? "expanded" : "collapsed"}
    >
      {/* Decorative blobs */}
      <div
        aria-hidden="true"
        className="absolute -top-20 -right-16 h-56 w-56 rounded-full bg-brand/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-gold/15 blur-3xl"
      />

      <div className="relative p-6 sm:p-8 lg:p-10">
        {/* Collapsed / intro row */}
        <div
          className={`grid lg:grid-cols-12 gap-6 lg:gap-10 items-center transition-all duration-500 ${
            done ? "opacity-0 h-0 overflow-hidden pointer-events-none" : "opacity-100"
          }`}
        >
          <div className="lg:col-span-8">
            <p
              className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.22em] text-gold mb-3"
              data-testid="custom-banner-overline"
            >
              <Sparkles size={12} className="animate-pulse-soft" />
              {eyebrow || "Flexible Options"}
            </p>
            <h3
              className={`font-heading ${
                compact
                  ? "text-2xl sm:text-3xl"
                  : "text-3xl sm:text-4xl lg:text-5xl"
              } font-extrabold text-white text-balance leading-tight`}
            >
              Need something <span className="text-brand-400">custom?</span>
            </h3>
            <p className="mt-4 text-sm sm:text-base text-slate-300 max-w-xl leading-relaxed">
              Every other month, quarterly, multi-bin households, vacation
              rentals. Tell us what your home actually needs and we&apos;ll
              send you a custom quote.
            </p>
          </div>
          <div className="lg:col-span-4 lg:flex lg:justify-end">
            <button
              type="button"
              onClick={() => {
                setOpen((o) => !o);
                setTimeout(() => {
                  if (formRef.current) {
                    formRef.current.scrollIntoView({
                      behavior: reduced ? "auto" : "smooth",
                      block: "center",
                    });
                    const firstInput = formRef.current.querySelector("input");
                    if (firstInput) firstInput.focus({ preventScroll: true });
                  }
                }, 420);
              }}
              className={`inline-flex items-center justify-center gap-2 h-12 sm:h-14 rounded-xl bg-white text-slate-900 font-bold px-6 sm:px-7 text-sm sm:text-base shadow-lift transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_20px_60px_-20px_rgba(212,160,23,0.55)] active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-white/30 ${
                !reduced && !open ? "animate-cta-bob" : ""
              }`}
              data-testid="custom-banner-toggle"
            >
              {open ? (
                <>
                  <X size={16} />
                  Hide form
                </>
              ) : (
                <>
                  <Wand2 size={16} className="text-brand-600" />
                  Request a Custom Quote
                </>
              )}
            </button>
          </div>
        </div>

        {/* Success state */}
        {done && (
          <div
            className="flex flex-col sm:flex-row items-start sm:items-center gap-5 sm:gap-6"
            data-testid="custom-banner-success"
          >
            <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-accent text-white shadow-lift">
              <CheckCircle2 size={26} />
            </span>
            <div className="flex-1">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-accent-300 mb-2">
                Request received
              </p>
              <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-white leading-tight">
                Thank you.
              </h3>
              <p className="mt-2 text-sm text-slate-300 max-w-xl">
                We received your custom quote request and will get back to you
                soon.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setDone(false);
                setOpen(false);
              }}
              className="text-sm font-semibold text-slate-300 hover:text-white transition-colors underline underline-offset-4"
              data-testid="custom-banner-reset"
            >
              Submit another
            </button>
          </div>
        )}

        {/* Expanding form */}
        <div
          ref={formRef}
          className={`grid transition-[grid-template-rows,opacity,margin] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
            open && !done
              ? "grid-rows-[1fr] opacity-100 mt-8"
              : "grid-rows-[0fr] opacity-0 mt-0"
          } ${reduced ? "transition-none" : ""}`}
          aria-hidden={!open || done}
          data-testid="custom-banner-form-wrap"
        >
          <div className="overflow-hidden">
            <form
              onSubmit={onSubmit}
              noValidate
              className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 sm:p-8"
              data-testid="custom-banner-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-5">
                <StaggerField delay={fieldDelay(0)} colspan="sm:col-span-3">
                  <BannerField
                    id="cq-name"
                    label="Full name"
                    value={form.full_name}
                    onChange={(e) => update("full_name", e.target.value)}
                    error={errors.full_name}
                    placeholder="Jane Smith"
                    testId="custom-banner-input-name"
                  />
                </StaggerField>
                <StaggerField delay={fieldDelay(1)} colspan="sm:col-span-3">
                  <BannerField
                    id="cq-phone"
                    label="Phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    error={errors.phone}
                    placeholder="(945) 272-2414"
                    testId="custom-banner-input-phone"
                  />
                </StaggerField>
                <StaggerField delay={fieldDelay(2)} colspan="sm:col-span-6">
                  <BannerField
                    id="cq-email"
                    label="Email"
                    type="email"
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    error={errors.email}
                    placeholder="you@email.com"
                    testId="custom-banner-input-email"
                  />
                </StaggerField>
                <StaggerField delay={fieldDelay(3)} colspan="sm:col-span-4">
                  <BannerField
                    id="cq-address"
                    label="Service address"
                    value={form.service_address}
                    onChange={(e) => update("service_address", e.target.value)}
                    error={errors.service_address}
                    placeholder="123 Maple St"
                    testId="custom-banner-input-address"
                  />
                </StaggerField>
                <StaggerField delay={fieldDelay(4)} colspan="sm:col-span-2">
                  <BannerField
                    id="cq-zip"
                    label="Zip code"
                    inputMode="numeric"
                    value={form.zip}
                    onChange={(e) =>
                      update("zip", e.target.value.replace(/\D/g, "").slice(0, 5))
                    }
                    error={errors.zip}
                    placeholder="08021"
                    testId="custom-banner-input-zip"
                  />
                </StaggerField>
                <StaggerField delay={fieldDelay(5)} colspan="sm:col-span-2">
                  <div>
                    <label
                      htmlFor="cq-bins"
                      className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 mb-2"
                    >
                      Bins
                    </label>
                    <select
                      id="cq-bins"
                      value={form.bin_count}
                      onChange={(e) =>
                        update("bin_count", parseInt(e.target.value, 10))
                      }
                      className="banner-input"
                      data-testid="custom-banner-input-bins"
                    >
                      {[1, 2, 3, 4, 5, 6].map((n) => (
                        <option key={n} value={n}>
                          {n} {n === 1 ? "bin" : "bins"}
                        </option>
                      ))}
                    </select>
                  </div>
                </StaggerField>
                <StaggerField delay={fieldDelay(6)} colspan="sm:col-span-6">
                  <div>
                    <label
                      htmlFor="cq-message"
                      className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 mb-2"
                    >
                      Service request <span className="text-gold">*</span>
                    </label>
                    <textarea
                      id="cq-message"
                      rows={5}
                      value={form.message}
                      onChange={(e) => update("message", e.target.value)}
                      placeholder="e.g. Once every two months, vacation rental setup, three large bins, etc."
                      className={`banner-input min-h-[140px] resize-y ${errors.message ? "border-red-400" : ""}`}
                      data-testid="custom-banner-input-message"
                    />
                    {errors.message && (
                      <p className="mt-1.5 text-xs text-red-300 inline-flex items-center gap-1.5">
                        <TriangleAlert size={12} />
                        {errors.message}
                      </p>
                    )}
                  </div>
                </StaggerField>
              </div>

              <div className="mt-7 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-xs text-slate-400">
                  We typically reply within 24 hours, Mon to Sat.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-brand text-white font-bold px-6 sm:px-7 text-sm sm:text-base transition-all duration-200 hover:bg-brand-600 hover:-translate-y-0.5 hover:shadow-lift active:scale-[0.98] focus:outline-none focus:ring-4 focus:ring-brand/40 disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="custom-banner-submit"
                >
                  {submitting ? "Sending…" : "Send Custom Quote Request"}
                  {!submitting && <Send size={14} />}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .banner-input {
          width: 100%;
          height: 3rem;
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.18);
          background: rgba(255,255,255,0.05);
          padding: 0.5rem 1rem;
          font-size: 16px;
          color: #fff;
          transition: border-color .2s, box-shadow .2s, background-color .2s;
        }
        .banner-input::placeholder {
          color: rgba(255,255,255,0.4);
        }
        .banner-input:focus {
          outline: none;
          border-color: #0EA5E9;
          background: rgba(255,255,255,0.08);
          box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.2);
        }
        @keyframes cta-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-cta-bob {
          animation: cta-bob 3.4s cubic-bezier(0.22, 1, 0.36, 1) infinite;
        }
        @keyframes pulse-soft {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .animate-pulse-soft {
          animation: pulse-soft 2.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-cta-bob, .animate-pulse-soft {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
}

function StaggerField({ delay, colspan, children }) {
  return (
    <div
      className={colspan}
      style={{
        transitionDelay: delay,
        transitionProperty: "opacity, transform",
        transitionDuration: "400ms",
        transitionTimingFunction: "cubic-bezier(0.22, 1, 0.36, 1)",
      }}
    >
      {children}
    </div>
  );
}

function BannerField({ id, label, error, testId, ...rest }) {
  return (
    <div>
      <label
        htmlFor={id}
        className="block text-[11px] font-bold uppercase tracking-[0.18em] text-slate-300 mb-2"
      >
        {label}
      </label>
      <input
        id={id}
        className={`banner-input ${error ? "border-red-400" : ""}`}
        data-testid={testId}
        {...rest}
      />
      {error && (
        <p className="mt-1.5 text-xs text-red-300 inline-flex items-center gap-1.5">
          <TriangleAlert size={12} />
          {error}
        </p>
      )}
    </div>
  );
}
